import React, { useCallback, useState } from 'react';
import VideocamIcon from '@material-ui/icons/Videocam';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import EditIcon from '@material-ui/icons/Edit';
import EmojiObjectsTwoToneIcon from '@material-ui/icons/EmojiObjectsTwoTone';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import { CustomButton } from '@/components/atoms/CustomButton';
import Loading from '@/components/molecules/Loading';

import { Camera } from '@/slicers/cameras';
import { Site } from '@/slicers/sites';
import {
  ModuleWith,
  PowerSwitch,
  PowerSwitchState,
  PowerUnitState,
  SwitchingState,
} from '@/slicers/operation_page';

import CameraItem from '../CameraItem';

import initState from './images/init-state.svg';
import unknowState from './images/unknow-state.svg';
import lock_svg from './images/lock-solid.svg';

import classes from './switches.module.scss';
import { unitMaxAmount } from '@/config';

interface SwitchesProps {
  disabled?: boolean;
  isConnected: boolean;
  site: Site;
  onChangeSwitchesState: (
    deviceCode: string,
    unitNumber: number,
    state: PowerSwitchState.ON | PowerSwitchState.OFF,
    switchNumbers: number[]
  ) => void;
  mdl: ModuleWith & {
    cameras: Camera[];
  };
  handleSaveModuleName: (id: number, name: string) => void;
  connectStatusInMobile: React.ReactElement | null;
  isHeader: boolean;
  connectStatus: React.ReactElement;
  isDeviceConnected?: boolean;
}

const lockTime = 500; //連続スイッチングを防止する
const SwtNums = [0, 1, 2, 3];
const UnitGroupOffsets = [
  1, // unit number
  5, // unit number
  9, // unit number
  13, // unit number
];
const ValidState = [PowerSwitchState.ON, PowerSwitchState.OFF];

export default function Switches({
  connectStatusInMobile,
  disabled,
  isConnected,
  site,
  mdl,
  onChangeSwitchesState,
  handleSaveModuleName,
  connectStatus,
  isDeviceConnected,
}: SwitchesProps) {
  const [groupIndex, setGroupIndex] = useState(0);
  const [isShowEditName, showEditName] = useState(false);
  const [editName, setEditName] = useState('');

  const [changedAt, setChangedAt] = useState(new Date().getTime());

  const handleClickGroupSwitch = async (
    unitNumber: number,
    state: PowerSwitchState.ON | PowerSwitchState.OFF
  ) => {
    if (disabled) return;

    const unit = mdl.unitsByNumber[unitNumber];

    const okSwitches: number[] = [];
    for (const swt of Object.values(unit.switchesByNumber)) {
      // Skip change state
      if (
        swt.isLock ||
        swt.switchingState === SwitchingState.SWITCHING ||
        !ValidState.includes(swt.state)
      )
        continue;

      const isOk = swt.requiredConfirmation
        ? window.confirm(`スイッチ番号${swt.name}を操作しますか?`)
        : true; // If there is no require confirm then it always ok to change state

      if (isOk) {
        okSwitches.push(swt.number);
      }

      // sleep tick before confirm next
      await new Promise((res) => setTimeout(res, 100));
    }

    const timestamp = new Date().getTime();
    if (timestamp - changedAt <= lockTime) return;

    setChangedAt(() => timestamp);

    if (okSwitches.length) {
      onChangeSwitchesState(mdl.deviceCode, unitNumber, state, okSwitches);
    }
  };

  const handleClickSwitchButton = (
    unitNumber: number,
    swt: PowerSwitch,
    nextState: PowerSwitchState.ON | PowerSwitchState.OFF
  ) => {
    if (
      disabled ||
      swt.isLock ||
      swt.switchingState === SwitchingState.SWITCHING ||
      !ValidState.includes(swt.state)
    )
      return;

    const isOk = swt.requiredConfirmation
      ? window.confirm('スイッチ操作をしますか？')
      : true; // If there is no require confirm then it always ok to change state

    if (!isOk) return;

    const timestamp = new Date().getTime();
    if (timestamp - changedAt <= lockTime) return;

    setChangedAt(() => timestamp);

    onChangeSwitchesState(mdl.deviceCode, unitNumber, nextState, [swt.number]);
  };

  const handleSaveNewName = useCallback(
    async (e) => {
      const newName = e.target.value;
      if (newName) {
        await handleSaveModuleName(mdl.id, newName);
      }
      showEditName(false);
      setEditName('');
    },
    [handleSaveModuleName, mdl.id]
  );

  const units = Array.from({ length: unitMaxAmount }).map((_, i) => {
    const unitNumber = UnitGroupOffsets[groupIndex] + i;
    return mdl.unitsByNumber?.[unitNumber];
  });

  const totalUnit = Object.keys(mdl.unitsByNumber).length;
  const totalGroup = Math.ceil(totalUnit / 4);

  const noConnectedMessage = isDeviceConnected ? null : (
    <p className={classes.noConnectedStatusMessage}>
      親機からの応答がありません。親機の電源が切れていないか、確認してください。
      親機の電源がオンにも関わらず応答がない場合は、親機のブレーカのオフオンをお願いします。
      電源オン後、親機の起動接続には約７分かかります。
    </p>
  );

  return (
    <div>
      <div className={classes.module}>
        <div className={classes.left}>
          <div className={classes.heading}>
            <div className={classes.headingName}>
              {isShowEditName ? (
                <input
                  maxLength={20}
                  className={classes.blurInput}
                  defaultValue={editName?.substr(0, 20)}
                  onBlur={handleSaveNewName}
                  placeholder="指令盤の名..."
                />
              ) : (
                <>
                  <span>{mdl.name?.substr(0, 20)}</span>
                  <EditIcon
                    onClick={() => {
                      showEditName(true);
                      setEditName(mdl!.name);
                    }}
                    htmlColor="#fff"
                  />
                </>
              )}
            </div>
            {!!connectStatusInMobile && (
              <div className={classes.headingStatus}>
                {connectStatusInMobile}
                {noConnectedMessage}
              </div>
            )}
          </div>
          <div className={classes.unitGroupChanger}>
            <CustomButton
              disabled={groupIndex < 1}
              color="info"
              onClick={() => setGroupIndex((prev) => prev - 1)}
            >
              {'<'}
            </CustomButton>
            <span>
              {groupIndex + 1}/{totalGroup}
            </span>
            <CustomButton
              disabled={
                groupIndex > UnitGroupOffsets.length - 2 ||
                groupIndex > totalGroup - 2
              }
              color="info"
              onClick={() => setGroupIndex((prev) => prev + 1)}
            >
              {'>'}
            </CustomButton>
          </div>
          <div className={classes.label}>
            {units.map((unit, i) => (
              // labelA -> labelD
              <div
                className={classes[`label${String.fromCharCode(65 + i)}`]}
                key={unit?.number ?? i}
              >
                {unit
                  ? String.fromCharCode(65 + (unit.number - 1))
                  : String.fromCharCode(65 + i)}
                グループ
              </div>
            ))}
          </div>
          <div className={classes.switches}>
            <div className={classes.switchesWrap}>
              {units.map((powerUnit, i) => {
                if (!powerUnit) {
                  return (
                    <div key={`empty-unit-${i}`} className={classes.unit}>
                      {SwtNums.map((swtNum) => {
                        return (
                          <div key={swtNum} className={classes.oneSwitch}>
                            <div className={classes.switch}>
                              <div
                                className={[classes.name, classes.empty].join(
                                  ' '
                                )}
                              >
                                <span>Empty</span>
                              </div>
                              <div className={classes.box}>
                                <div />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                return (
                  <div key={powerUnit.number} className={classes.unit}>
                    {SwtNums.map((switchNumber, j) => {
                      const powerSwitch: PowerSwitch =
                        powerUnit.switchesByNumber[switchNumber]!;

                      if (!powerSwitch) {
                        return (
                          <div key={switchNumber} className={classes.oneSwitch}>
                            <div className={classes.switch}>
                              <div
                                className={[classes.name, classes.empty].join(
                                  ' '
                                )}
                              >
                                <span>Empty</span>
                              </div>
                              <div className={classes.box}>
                                <div />
                              </div>
                            </div>
                          </div>
                        );
                      }

                      const nextState =
                        powerSwitch.state === PowerSwitchState.ON
                          ? PowerSwitchState.OFF
                          : PowerSwitchState.ON;

                      return (
                        <div
                          key={powerSwitch.number}
                          className={classes.oneSwitch}
                        >
                          <div
                            onClick={() =>
                              handleClickSwitchButton(
                                powerUnit.number,
                                powerSwitch,
                                nextState
                              )
                            }
                            className={[
                              classes.switch,
                              isConnected ? classes.clickable : '',
                              powerSwitch.state === PowerSwitchState.ON
                                ? classes.on
                                : '',
                            ].join(' ')}
                          >
                            <div className={classes.name}>
                              <span>{powerSwitch.name}</span>
                            </div>
                            <div
                              className={[
                                classes.box,
                                classes.shape,
                                powerSwitch.switchingState ===
                                  SwitchingState.SWITCHING &&
                                powerSwitch.state !== PowerSwitchState.UNKNOWN
                                  ? classes.switching
                                  : '',
                              ].join(' ')}
                            >
                              {powerSwitch.isLock ? (
                                <img
                                  alt=""
                                  src={lock_svg}
                                  style={{ width: '50%' }}
                                />
                              ) : powerSwitch.state ===
                                PowerSwitchState.INIT ? (
                                <img alt="" src={initState} />
                              ) : powerSwitch.state ===
                                PowerSwitchState.UNKNOWN ? (
                                <img alt="" src={unknowState} />
                              ) : powerSwitch.icon === 'light' ? (
                                <EmojiObjectsTwoToneIcon
                                  fontSize="large"
                                  style={{ fontSize: '3rem' }}
                                />
                              ) : powerSwitch.icon === 'up' ? (
                                <ArrowDropUpIcon fontSize="large" />
                              ) : powerSwitch.icon === 'down' ? (
                                <ArrowDropDownIcon fontSize="large" />
                              ) : (
                                powerSwitch.icon === 'stop' && (
                                  <div className={classes.stopIcon} />
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          <div className={classes.footer}>グループー括操作</div>
          <div className={classes.staticSwitches}>
            <div className={classes.staticSwitchesWrap}>
              {units.map((powerUnit, i) => {
                if (
                  !powerUnit ||
                  !Object.keys(powerUnit.switchesByNumber).length ||
                  !powerUnit.group_on_off_flg
                ) {
                  return (
                    <div key={`empty-gr-flg-${i}`}>
                      <div className={classes.oneSwitch}>
                        <div className={classes.staticSwitch} />
                      </div>
                      <div className={classes.oneSwitch}>
                        <div className={classes.staticSwitch} />
                      </div>
                    </div>
                  );
                }

                const isReady =
                  powerUnit && powerUnit.state === PowerUnitState.READY;

                const switchClass = isReady ? classes[`color${i + 1}`] : '';

                return (
                  <div key={powerUnit.number}>
                    <div className={classes.oneSwitch}>
                      <CustomButton
                        onClick={(event) =>
                          handleClickGroupSwitch(
                            powerUnit.number,
                            PowerSwitchState.ON
                          )
                        }
                        color="grey"
                        className={[
                          classes.staticSwitch,
                          classes.shadow,
                          switchClass,
                        ].join(' ')}
                      >
                        G{powerUnit.number}全ON
                      </CustomButton>
                    </div>
                    <div className={classes.oneSwitch}>
                      <CustomButton
                        onClick={() =>
                          handleClickGroupSwitch(
                            powerUnit.number,
                            PowerSwitchState.OFF
                          )
                        }
                        color="grey"
                        className={[
                          classes.staticSwitch,
                          classes.shadow,
                          switchClass,
                        ].join(' ')}
                      >
                        G{powerUnit.number}全OFF
                      </CustomButton>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className={classes.right}>
          <div className={classes.header}>
            <div className={classes.headInfo}>
              <div className={classes.siteName}>
                <span>{site?.name}</span>
              </div>
              {!connectStatusInMobile && connectStatus}
            </div>
          </div>
          {!connectStatusInMobile && noConnectedMessage}
          {/* <div className={classes.listHeading}>
            <div>
              <span>
                <VideocamIcon />
              </span>
            </div>
            <div className={classes.title}>カメラ確認</div>
            <div>
              <CheckCircleIcon htmlColor="#FF6969" />
            </div>
          </div>
          <div className={classes.listCamera}>
            {mdl.cameras
              .filter((camera) => camera.is_valid)
              .map((camera) => (
                <CameraItem
                  key={camera.id}
                  camera={camera}
                  siteId={site?.id}
                  siteName={site?.name}
                />
              ))}
          </div> */}
        </div>
      </div>
      {mdl.switchingState === SwitchingState.SWITCHING ? (
        <Loading visible text="同期中..." />
      ) : null}
    </div>
  );
}
